import { LOG_CUSTOM_ATTRIBUTE, LOG_INFO, logger } from 'config/logger';
import { getExtensionStatus } from 'hooks/shared/useBrowserExtensionStatusQuery';
import { useCallback, useEffect, useRef } from 'react';
import { default as useFeatureToggles } from 'shared/legacy/hooks/useFeatureToggles';
import { useCampaignStore } from 'stores/campaign/campaign.store';
import { createContext, useContextSelector } from 'use-context-selector';
import { notImplemented } from './notImplemented';

const getStackId = (): string => {
  try {
    const stack = new Error().stack;
    const match = stack
      ?.split('\n')
      .slice(3, 9)
      .map((l) => l.trim())
      .join('|');
    return hashString(match ?? 'unknown');
  } catch {
    return Math.random().toString(36).substring(2, 8);
  }
};

const hashString = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return `h${Math.abs(hash)}`;
};

const createLogMessage = (label: string, value: any): [string, string] => {
  let valueStr: string;
  try {
    valueStr = typeof value === 'string' ? value : JSON.stringify(value);
  } catch (e) {
    valueStr = e instanceof Error ? e.message : typeof e === 'object' ? JSON.stringify(e) : 'error cannot be parsed';
  }

  const stackId = getStackId();

  return [stackId, `[${stackId}] ${label}: ${valueStr}`] as const;
};

const DevLogManagerContext = createContext<{ devLogManager: (label: string, value: any) => void }>({
  devLogManager: notImplemented,
});

export const DevLogManagerContextProvider = ({ children }: { children: React.ReactNode }) => {
  const callLaterRef = useRef<string[]>([]);
  const prevValuesRef = useRef(new Map<string, any>());

  const {
    isFeatureToggleEnabled,
    isLoading: isFeatureTogglesLoading,
    dataUpdatedAt: featureTogglesDataUpdatedAt,
  } = useFeatureToggles();
  const isDevLogManagerEnabled = isFeatureToggleEnabled((f) => f.DEV_LOG_MANAGER);

  useEffect(() => {
    if (isDevLogManagerEnabled) {
      const currentLogsInQueue = callLaterRef.current.splice(0, callLaterRef.current.length);

      currentLogsInQueue.forEach((log) => logger.info(`[call later] ${log}`, LOG_INFO.MANUAL_LOG_LOCAL));
    }
  }, [isDevLogManagerEnabled]);

  const devLogManager = useCallback(
    (label: string, value: any) => {
      const prevValues = prevValuesRef.current;
      const [stackId, logMessage] = createLogMessage(label, value);

      if (prevValues.get(stackId) === value) return;
      prevValues.set(stackId, value);

      if (featureTogglesDataUpdatedAt === 0 || isFeatureTogglesLoading) {
        return callLaterRef.current.push(logMessage);
      }

      if (!isDevLogManagerEnabled) {
        return;
      }

      logger.info(logMessage, LOG_INFO.MANUAL_LOG_LOCAL);
    },
    [featureTogglesDataUpdatedAt, isDevLogManagerEnabled],
  );

  useEffect(() => {
    getExtensionStatus().then((data) => {
      logger.setCustomAttribute(LOG_CUSTOM_ATTRIBUTE.RR_EXTENSION_VERSION, data.version ?? 'n/a');
      logger.setCustomAttribute(LOG_CUSTOM_ATTRIBUTE.RR_EXTENSION_STATUS, data.status ?? 'n/a');
    });
  }, []);

  useEffect(() => {
    return useCampaignStore.subscribe((s) => {
      devLogManager('store.campaign', s.campaign);
    });
  }, []);

  return <DevLogManagerContext.Provider value={{ devLogManager }}>{children}</DevLogManagerContext.Provider>;
};

export const useDevLogManager = (label: string, value: any) => {
  const devLogManager = useContextSelector(DevLogManagerContext, (s) => s.devLogManager);

  devLogManager(label, value);
};

export const useGetDevLogManager = () => {
  const devLogManager = useContextSelector(DevLogManagerContext, (s) => s.devLogManager);

  return devLogManager;
};
